import { Capacitor } from '@capacitor/core';
import {
  Geolocation,
  type PermissionStatus,
  type Position,
  type PositionOptions,
} from '@capacitor/geolocation';
import { ref, type Ref } from 'vue';

export type { Position };

export interface UseCurrentLocationOptions extends PositionOptions {
  /**
   * On iOS/Android, call `Geolocation.requestPermissions()` before reading position.
   * No-op on web. Default true.
   */
  requestPermissionsOnNative?: boolean;
}

export interface UseCurrentLocationReturn {
  position: Ref<Position | null>;
  permissionStatus: Ref<PermissionStatus | null>;
  error: Ref<string | null>;
  isLoading: Ref<boolean>;
  /** Resolves with the position, or throws (error is also set on the ref). */
  getCurrentPosition: () => Promise<Position>;
  /** Native-only; on web resolves with whatever `checkPermissions` returns. */
  checkPermissions: () => Promise<PermissionStatus>;
  /** Native-only; on web is a no-op that resolves immediately. */
  requestPermissions: () => Promise<PermissionStatus>;
}

const defaultPositionOptions: PositionOptions = {
  enableHighAccuracy: true,
  // Capacitor applies this timeout too. The race below still stops a hung native request.
  timeout: 15_000,
  maximumAge: 0,
};

export function useCurrentLocation(
  options: UseCurrentLocationOptions = {},
): UseCurrentLocationReturn {
  const { requestPermissionsOnNative = true, ...positionOptions } = options;

  const position = ref<Position | null>(null) as Ref<Position | null>;
  const permissionStatus = ref<PermissionStatus | null>(null) as Ref<PermissionStatus | null>;
  const error = ref<string | null>(null);
  const isLoading = ref(false);

  const mergedOptions: PositionOptions = {
    ...defaultPositionOptions,
    ...positionOptions,
  };

  const timeoutMs = mergedOptions.timeout ?? defaultPositionOptions.timeout!;

  const checkPermissions = async (): Promise<PermissionStatus> => {
    const status = await Geolocation.checkPermissions();
    permissionStatus.value = status;
    return status;
  };

  const requestPermissions = async (): Promise<PermissionStatus> => {
    if (!Capacitor.isNativePlatform()) {
      return checkPermissions();
    }
    const status = await Geolocation.requestPermissions();
    permissionStatus.value = status;
    return status;
  };

  const fetchPosition = (positionOpts: PositionOptions): Promise<Position> =>
    Promise.race([
      Geolocation.getCurrentPosition(positionOpts),
      new Promise<Position>((_, reject) => {
        setTimeout(() => reject(new Error(`Location timed out after ${timeoutMs}ms`)), timeoutMs);
      }),
    ]);

  const getCurrentPosition = async (): Promise<Position> => {
    isLoading.value = true;
    error.value = null;

    try {
      if (Capacitor.isNativePlatform() && requestPermissionsOnNative) {
        await requestPermissions();
      }

      try {
        const pos = await fetchPosition(mergedOptions);
        position.value = pos;
        return pos;
      } catch (firstError) {
        if (!mergedOptions.enableHighAccuracy) {
          throw firstError;
        }
        // iOS high-accuracy requestLocation can hang or fail indoors; fall back to coarse.
        const pos = await fetchPosition({ ...mergedOptions, enableHighAccuracy: false });
        position.value = pos;
        return pos;
      }
    } catch (e) {
      position.value = null;
      const message = e instanceof Error ? e.message : String(e);
      error.value = message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    position,
    permissionStatus,
    error,
    isLoading,
    getCurrentPosition,
    checkPermissions,
    requestPermissions,
  };
}
